// Analysis Panel — multi-vendor consensus review, per mockup
// screen9-consensus.png. Story #5116 Streams 9c/9d/9e (Task #5157). Renders
// the real author (Anthropic) / verifier (Amazon Nova Pro) / skeptic
// (Mistral Large 3) sequential orchestration lambda/innovateflow-api/
// whitespace/panel.py implements (Task #5147, run_panel()) — a partial
// result names exactly which role failed and why, dissent is always shown
// per-claim, and nothing here fabricates a vendor card or verdict the
// backend didn't actually return.
//
// DOCUMENTED DIFFERENCE from the mockup: the mockup's vendor names and
// claim-grid column initials (Claude Opus 5, Amazon Nova Premier, Meta
// Llama 4 Maverick, "C/N/L") are placeholders from an earlier design pass.
// The DEPLOYED backend (whitespace/panel_vendors.py's ROLE_MODEL_IDS, plus
// this story's own live-evidence capture at tests/live-evidence/
// 5113-panel-run.json) resolves to different, real model ids — see
// ROLE_INFO below. This screen's provenance footer names those ACTUAL ids,
// per this task's own acceptance criteria, never the mockup's placeholders.
import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, RefreshCw, ShieldCheck, Users, ListChecks, X } from 'lucide-react'
import { requestAnalysisPanel } from '../../services/api'
import type { WhitespacePanelConsensusLevel, WhitespacePanelResult, WhitespacePanelRole } from '../../types/whitespace'
import LoadingSkeleton from '../shared/LoadingSkeleton'
import EmptyState from '../shared/EmptyState'

// Real, live-verified Bedrock model ids per role — never placeholder vendor
// names. Verifier/skeptic each resolve to exactly one fixed model id
// (panel_vendors.py's own docstring: "no tiering needed... one fixed,
// live-verified model id each"). Author's id is nominally tier-selected,
// but the panel route's _call_vendor() resolves it with no tier override
// (whitespace/panel.py), so it always resolves to this same id today — the
// exact id this story's live-evidence capture recorded for every author call.
const ROLE_INFO: Record<
  WhitespacePanelRole,
  { label: string; vendor: string; modelId: string; blurb: string; initial: string }
> = {
  author: {
    label: 'Author',
    vendor: 'Anthropic',
    modelId: 'au.anthropic.claude-sonnet-4-5-20250929-v1:0',
    blurb: 'evidence-grounded generation',
    initial: 'A',
  },
  verifier: {
    label: 'Verifier',
    vendor: 'Amazon Nova Pro',
    modelId: 'amazon.nova-pro-v1:0',
    blurb: 'cross-vendor claim tracing',
    initial: 'V',
  },
  skeptic: {
    label: 'Skeptic',
    vendor: 'Mistral Large 3',
    modelId: 'mistral.mistral-large-3-675b-instruct',
    blurb: 'instructed to argue against the opportunity',
    initial: 'S',
  },
}

const CONSENSUS_STYLE: Record<WhitespacePanelConsensusLevel, string> = {
  STRONG: 'bg-green-50 text-green-700 border border-green-200',
  AMBER: 'bg-amber-50 text-amber-700 border border-amber-200',
  DISSENT: 'bg-red-50 text-red-700 border border-red-200',
}

function skepticMarkCell(agree: boolean | null | undefined) {
  if (agree === true) return { symbol: '✓', className: 'text-green-600' }
  if (agree === false) return { symbol: '✗', className: 'text-red-600' }
  return { symbol: '—', className: 'text-gray-400' }
}

interface AnalysisPanelProps {
  projectId: string
  /** The card's already-resolved boundary record ids (patent_record_ids +
   * academic_record_ids) — never a probe_id or band key
   * (whitespace/panel.py's create_panel docstring). */
  recordIds: string[]
  /** Short region label for the panel header, e.g. regionLabel(region). */
  title: string
  onClose: () => void
}

export default function AnalysisPanel({ projectId, recordIds, title, onClose }: AnalysisPanelProps) {
  const [panel, setPanel] = useState<WhitespacePanelResult | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRerunning, setIsRerunning] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const requestedFor = useRef<string | null>(null)
  const recordIdsKey = recordIds.join(',')

  const runPanel = async (force: boolean) => {
    setFetchError(null)
    if (force) setIsRerunning(true)
    else setIsLoading(true)
    try {
      const response = await requestAnalysisPanel(projectId, recordIds, force)
      setPanel(response.panel)
    } catch (e) {
      setFetchError((e as Error)?.message ?? 'Failed to run the analysis panel.')
    } finally {
      setIsLoading(false)
      setIsRerunning(false)
    }
  }

  useEffect(() => {
    const key = `${projectId}:${recordIdsKey}`
    if (requestedFor.current === key) return
    requestedFor.current = key
    runPanel(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, recordIdsKey])

  const claims = panel?.claims ?? []
  const consensus = panel?.consensus ?? []
  const verdicts = panel?.verdicts ?? []
  const agreement = panel?.agreement ?? []
  const agreementByClaim = new Map(agreement.map((a) => [a.claim_id, a]))
  const completedRoles = panel?.completed_roles ?? []

  const strongCount = consensus.filter((c) => c.level === 'STRONG').length
  const verifiedCount = verdicts.filter((v) => v.mark === 'VERIFIED').length
  const agreeCount = agreement.filter((a) => a.agree).length
  const unresolvedCount = consensus.filter((c) => c.level !== 'STRONG').length

  const skepticVerdictLabel =
    agreement.length === 0
      ? 'NO AGREEMENT DATA'
      : agreeCount === agreement.length
        ? 'FULL AGREEMENT'
        : agreeCount === 0
          ? 'FULL DISSENT'
          : `PARTIAL DISSENT ${agreeCount}/${agreement.length}`

  return (
    <div className="light-card p-4" data-testid="analysis-panel">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="text-sm font-bold text-iflow-900 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4" /> Analysis panel — {title}
          </div>
          <p className="text-xs text-gray-600 mt-0.5">Three vendors, one evidence pack — agreement shown per claim.</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {panel?.status === 'complete' && (
            <span
              className="px-2 py-1 text-[11px] font-semibold rounded-md bg-sky-500/10 text-iflow-700"
              data-testid="panel-consensus-badge"
            >
              PANEL CONSENSUS {strongCount}/{consensus.length} STRONG
            </span>
          )}
          <button
            onClick={() => runPanel(true)}
            disabled={isLoading || isRerunning}
            className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium border border-sky-200 text-gray-700 hover:bg-sky-50 rounded-md transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${isRerunning ? 'animate-spin' : ''}`} />
            Re-run panel
          </button>
          <button onClick={onClose} aria-label="Close analysis panel" className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isLoading && <LoadingSkeleton type="card" rows={3} />}

      {!isLoading && fetchError && (
        <div className="p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg" role="alert">
          {fetchError}
        </div>
      )}

      {!isLoading && !fetchError && panel?.status === 'error' && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg" role="alert" data-testid="panel-error">
          Couldn't run the analysis panel —{' '}
          {panel.failed_reason === 'no_grounding_text'
            ? "no evidence text could be retrieved for this opportunity's records."
            : (panel.failed_reason ?? 'unknown error.')}
        </div>
      )}

      {!isLoading && !fetchError && panel && panel.status !== 'error' && (
        <>
          {panel.status === 'partial' && (
            <div
              className="mb-3 p-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg flex items-center gap-1.5"
              role="alert"
              data-testid="panel-partial-banner"
            >
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              The {panel.failed_role} vendor call didn't complete ({panel.failed_reason}) — showing the{' '}
              {completedRoles.length} role{completedRoles.length === 1 ? '' : 's'} that did.
            </div>
          )}

          {completedRoles.length === 0 && (
            <div data-testid="panel-no-roles">
              <EmptyState
                icon={<Users className="w-4 h-4" />}
                title="No vendor completed"
                description="Every vendor call stopped before returning a result — re-run the panel to try again."
                compact
              />
            </div>
          )}

          {completedRoles.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-4">
              <div className="space-y-3">
                {(['author', 'verifier', 'skeptic'] as WhitespacePanelRole[])
                  .filter((role) => completedRoles.includes(role))
                  .map((role) => {
                    const info = ROLE_INFO[role]
                    return (
                      <div key={role} className="border border-sky-100 rounded-lg p-3" data-testid={`panel-vendor-card-${role}`}>
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-iflow-700 text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                              {info.initial}
                            </span>
                            <div>
                              <div className="text-xs font-bold text-iflow-900">
                                {info.label} · {info.vendor}
                              </div>
                              <div className="text-[10px] text-gray-500 uppercase tracking-wide">{info.blurb}</div>
                            </div>
                          </div>
                          <span
                            className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-sky-500/10 text-iflow-700 flex-shrink-0 text-right"
                            data-testid={`panel-verdict-chip-${role}`}
                          >
                            {role === 'author' && `${claims.length} CLAIM${claims.length === 1 ? '' : 'S'} PROPOSED`}
                            {role === 'verifier' && `VERIFIED ${verifiedCount}/${verdicts.length}`}
                            {role === 'skeptic' && skepticVerdictLabel}
                          </span>
                        </div>
                        {role === 'author' && <p className="text-xs text-gray-700 leading-relaxed">{panel.statement}</p>}
                        {role === 'skeptic' && panel.dissent && (
                          <p className="text-xs text-gray-700 leading-relaxed">{panel.dissent}</p>
                        )}
                        {role === 'verifier' && (
                          <p className="text-xs text-gray-700 leading-relaxed">
                            Marked every claim VERIFIED, QUESTIONABLE, or CONTRADICTED against the evidence pack — see
                            the Verifier column in the grid.
                          </p>
                        )}
                      </div>
                    )
                  })}
              </div>

              <div>
                <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Claim-by-claim agreement
                </div>
                {consensus.length === 0 && (
                  <div data-testid="panel-claims-empty">
                    <EmptyState
                      icon={<ListChecks className="w-4 h-4" />}
                      title="No claims to compare yet"
                      description="The panel didn't produce any claims to check agreement on."
                      compact
                    />
                  </div>
                )}
                {consensus.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs" data-testid="panel-claim-grid">
                      <thead>
                        <tr className="border-b border-gray-200 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                          <th className="py-1.5 pr-2">Claim</th>
                          <th className="py-1.5 px-1 text-center">Verifier</th>
                          <th className="py-1.5 px-1 text-center">Skeptic</th>
                          <th className="py-1.5 pl-2 text-right">Consensus</th>
                        </tr>
                      </thead>
                      <tbody>
                        {consensus.map((entry) => {
                          const agreementEntry = agreementByClaim.get(entry.claim_id)
                          const skepticMark = skepticMarkCell(agreementEntry?.agree)
                          return (
                            <tr key={entry.claim_id} className="border-b border-gray-100 align-top" data-testid="panel-claim-row">
                              <td className="py-1.5 pr-2 text-gray-800">{entry.claim_text}</td>
                              <td className="py-1.5 px-1 text-center text-gray-600" title={entry.verifier_reason ?? undefined}>
                                {entry.verifier_mark ?? '—'}
                              </td>
                              <td className={`py-1.5 px-1 text-center font-bold ${skepticMark.className}`}>{skepticMark.symbol}</td>
                              <td className="py-1.5 pl-2 text-right">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${CONSENSUS_STYLE[entry.level]}`}>
                                  {entry.level}
                                </span>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {unresolvedCount > 0 && (
                  <div
                    className="mt-2 p-2 bg-amber-50 border border-amber-200 text-amber-800 text-[11px] rounded-lg"
                    data-testid="panel-dissent-notice"
                  >
                    {unresolvedCount} claim{unresolvedCount === 1 ? '' : 's'} {unresolvedCount === 1 ? 'lacks' : 'lack'}{' '}
                    full agreement — shown amber/dissent above, never averaged into the consensus badge.
                  </div>
                )}
              </div>
            </div>
          )}

          {completedRoles.length > 0 && (
            <div className="mt-4 pt-3 border-t border-sky-100" data-testid="panel-provenance-footer">
              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Panel provenance — real resolved model ids
              </div>
              <div className="space-y-1">
                {completedRoles.map((role) => {
                  const info = ROLE_INFO[role]
                  return (
                    <div key={role} className="text-[10.5px] text-gray-600 flex items-center gap-1.5 flex-wrap">
                      <b className="text-gray-700">{info.label}</b>
                      <span className="font-mono text-gray-500">{info.modelId}</span>
                      <span className="text-gray-400">· {info.blurb}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
